@if (loading()) {
  <div class="container-fluid py-6 text-center text-muted">A carregar…</div>
} @else if (error()) {
  <!-- Com saída: um link antigo ou escrito à mão deixava a pessoa num beco só com o aviso. -->
  <div class="container-fluid py-6 text-center">
    <p class="text-danger mb-3">{{ error() }}</p>
    <a class="btn btn-sm btn-outline-secondary" routerLink="..">Voltar às ocorrências</a>
  </div>
} @else if (occurrence(); as item) {
  <div class="container-fluid">
    <app-page-header
      icon="fe-file-text"
      [pretitle]="item.code"
      [title]="item.title"
      description="Detalhes, prazos e situação atual da ocorrência."
    >
      <div actions>
        <a class="btn btn-outline-light me-2" routerLink="..">Voltar</a>
        @if (auth.can('occurrence:write')) {
          <!-- Uma ocorrência nova parecida com esta, mas sem ligação a ela (revisão de
               2026-10-13: "às vezes tenho OC muito semelhantes, mas que não estão relacionadas"). -->
          <a class="btn btn-white me-2" routerLink="../nova" [queryParams]="{ replicarDe: item.id }"
             title="Registar uma ocorrência nova com a classificação e a descrição desta">
            <i class="fe fe-copy me-1"></i> Replicar
          </a>
          <a class="btn btn-white" routerLink="editar"><i class="fe fe-edit-2 me-1"></i> Editar</a>
        }
      </div>
    </app-page-header>
  </div>

  <div class="container-fluid">
    @if (sensitive(item)) {
      <!-- Denúncias e afins: o circuito é reservado e isso tem de se ver antes de tudo o resto. -->
      <div class="alert alert-danger d-flex align-items-center gap-2 mb-4" role="status">
        <i class="fe fe-lock"></i>
        <span>
          <strong>Tratamento confidencial.</strong>
          Partilhe esta ocorrência apenas com quem trata do circuito de denúncias.
          @if (item.participantIdentification === 'ANONIMO') { A participação é anónima — não há como contactar quem participou. }
        </span>
      </div>
    }
    <div class="row">
      <div class="col-12 col-xl-8">
        <!-- As secções são as da especificação e estão cá todas, mesmo por preencher: o que
             falta aparece a "—", para se ver o que ainda há a fazer sem abrir a edição. Cada
             "Editar" abre o separador da sua secção. -->

        <!-- Identificação -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-hash"></i></span></span>
              <h4 class="card-header-title mb-0">Identificação</h4>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span class="badge" [class]="badgeClass(item)">{{ stateBadgeLabel(item) }}</span>
              @if (auth.can('occurrence:write')) {
                <a class="btn btn-sm btn-white" routerLink="editar" [queryParams]="{ step: 'identificacao' }">
                  <i class="fe fe-edit-2 me-1"></i> Editar
                </a>
              }
            </div>
          </div>
          <div class="card-body">

            <!-- Ciclo de vida: posição do estado atual no fluxo -->
            <div class="occ-steps mb-4">
              @for (step of stateFlow; track step) {
                <div
                  class="occ-step"
                  [class.occ-step--done]="stepStatus(item, step) === 'done'"
                  [class.occ-step--current]="stepStatus(item, step) === 'current'"
                  [class.occ-step--closed-fail]="stepStatus(item, step) === 'current' && item.situation === 'FECHADO' && item.closingType === 'SEM_SUCESSO'"
                  [class.occ-step--closed-ok]="stepStatus(item, step) === 'current' && item.situation === 'FECHADO' && item.closingType === 'COM_SUCESSO'"
                >
                  <span class="occ-step-dot">
                    @if (stepStatus(item, step) === 'done') { <i class="fe fe-check"></i> }
                    @else if (item.situation === 'FECHADO' && stepStatus(item, step) === 'current') { <i class="fe fe-flag"></i> }
                  </span>
                  <span class="occ-step-label">{{ stateLabels[step] }}</span>
                </div>
              }
            </div>

            <div class="row g-3">
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date">
                  <i class="fe fe-hash"></i>
                  <div><span class="occ-date-label">ID da ocorrência</span><span class="occ-date-value">{{ item.code }}</span></div>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date">
                  <i class="fe fe-clock"></i>
                  <div><span class="occ-date-label">Data de registo</span><span class="occ-date-value">{{ item.createdAt | date: 'dd/MM/yyyy HH:mm' }}</span></div>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date">
                  <i class="fe fe-user"></i>
                  <div><span class="occ-date-label">Registado por</span><span class="occ-date-value">{{ item.registeredByName || '—' }}</span></div>
                </div>
              </div>
              @if (item.criticalCaseId) {
                <div class="col-12 col-sm-6 col-lg-4">
                  <div class="occ-date">
                    <i class="fe fe-alert-octagon"></i>
                    <div>
                      <span class="occ-date-label">Caso crítico</span>
                      <span class="occ-date-value">
                        <a [routerLink]="['/', auth.currentTenant(), 'casos-criticos', item.criticalCaseId]">Caso crítico #{{ item.criticalCaseId }}</a>
                      </span>
                    </div>
                  </div>
                </div>
              }
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date">
                  <i class="fe" [class]="originIcons[item.origin]"></i>
                  <div>
                    <span class="occ-date-label">Origem</span>
                    <span class="occ-date-value">
                      {{ originLabels[item.origin] }}@if (item.originOther) { — {{ item.originOther }} }
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date">
                  <i class="fe fe-toggle-left"></i>
                  <div><span class="occ-date-label">Situação</span><span class="occ-date-value">{{ situationLabels[item.situation] }}</span></div>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date">
                  <i class="fe fe-activity"></i>
                  <div><span class="occ-date-label">Estado</span><span class="occ-date-value">{{ stateLabels[item.state] }}</span></div>
                </div>
              </div>
              <!-- O código de acompanhamento é o que se entrega a quem participou (e à equipa de
                   RH, quando a ocorrência vem do Essentials) para seguir o caso de fora. Só
                   existe no que entra pelo portal ou pelo Essentials: numa ocorrência registada
                   de dentro, a linha não aparece. -->
              @if (item.trackingCode) {
                <div class="col-12 col-lg-8">
                  <div class="occ-date">
                    <i class="fe fe-key"></i>
                    <div>
                      <span class="occ-date-label">Código de acompanhamento</span>
                      <span class="occ-date-value"><code>{{ item.trackingCode }}</code></span>
                      <span class="occ-date-hint">
                        É com este código que quem participou segue o caso de fora, sem conta.
                      </span>
                    </div>
                  </div>
                </div>
              } @else if (item.nature === 'DENUNCIA' || item.nature === 'RECLAMACAO') {
                <!-- Pelo site e pelo Essentials o identificador nasce com o registo. A denúncia
                     ou reclamação registada à mão ficava sem ele (documento de 2026-10-08). -->
                <div class="col-12 col-lg-8">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="dar-acompanhamento"
                           [checked]="false"
                           [disabled]="startingTracking() || !auth.can('occurrence:write')"
                           (change)="startTracking()" />
                    <label class="form-check-label" for="dar-acompanhamento">
                      Informar quem comunicou e dar acompanhamento
                      <span class="text-muted d-block small">
                        Gera o identificador e envia o e-mail de confirmação. A partir daí, quem
                        comunicou segue a situação, o estado, a triagem e o desfecho pelo
                        identificador.
                      </span>
                    </label>
                  </div>
                </div>
              }
            </div>

            <!-- Fecho: tipo, data, desfecho e a descrição do resultado -->
            <hr class="my-4" />
            <h6 class="text-muted text-uppercase mb-3"><i class="fe fe-archive me-1"></i> Fecho</h6>
            @if (item.situation !== 'FECHADO') {
              <p class="text-muted mb-0">A ocorrência está aberta — o fecho preenche-se em "Mudar estado".</p>
            } @else {
              <div class="row g-3">
                <div class="col-12 col-sm-6 col-lg-4">
                  <div class="occ-date" [class.occ-date--ok]="item.closingType === 'COM_SUCESSO'" [class.occ-date--fail]="item.closingType === 'SEM_SUCESSO'">
                    <i class="fe fe-flag"></i>
                    <div>
                      <span class="occ-date-label">Tipo de fecho</span>
                      <span class="occ-date-value">{{ item.closingType ? closingTypeLabels[item.closingType] : '—' }}</span>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-lg-4">
                  <div class="occ-date">
                    <i class="fe fe-calendar"></i>
                    <div><span class="occ-date-label">Data de fecho</span><span class="occ-date-value">{{ (item.closingDate | date: 'dd/MM/yyyy') || '—' }}</span></div>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-lg-4">
                  <div class="occ-date">
                    <i class="fe fe-check-square"></i>
                    <div>
                      <span class="occ-date-label">Desfecho</span>
                      <span class="occ-date-value">{{ item.outcome ? outcomeLabels[item.outcome] : '—' }}</span>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-sm-6 col-lg-4">
                  <div class="occ-date">
                    <i class="fe fe-lock"></i>
                    <div>
                      <span class="occ-date-label">Conservação até</span>
                      <span class="occ-date-value">{{ (item.retentionUntil | date: 'dd/MM/yyyy') || '—' }}</span>
                      @if (item.legalHold) {
                        <span class="small text-muted d-block">
                          Prorrogada: há processos pendentes@if (item.legalHoldNote) { — {{ item.legalHoldNote }}}
                        </span>
                      }
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <h6 class="text-muted text-uppercase mb-2"><i class="fe fe-align-left me-1"></i> Descrição do resultado</h6>
                  <p class="mb-0" style="white-space: pre-wrap;">{{ item.closingResult || '—' }}</p>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Caracterização -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-tag"></i></span></span>
              <h4 class="card-header-title mb-0">Caracterização</h4>
            </div>
            @if (auth.can('occurrence:write')) {
              <a class="btn btn-sm btn-white" routerLink="editar" [queryParams]="{ step: 'caracterizacao' }">
                <i class="fe fe-edit-2 me-1"></i> Editar
              </a>
            }
          </div>
          <div class="card-body">

            <!-- Classificação: tiles com assinatura de cor (âmbito=navy, tipo=âmbar, origem=cinza) -->
            <div class="row g-3 mb-1">
              <div class="col-sm-6">
                <div class="occ-tile">
                  <span class="occ-tile-icon occ-tile-icon--scope"><i class="fe" [class]="scopeIcons[item.scope]"></i></span>
                  <div class="occ-tile-body">
                    <span class="occ-tile-label">Âmbito</span>
                    <span class="occ-tile-value">{{ scopeLabels[item.scope] }}@if (item.scopeOther) { — {{ item.scopeOther }} }</span>
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="occ-tile">
                  <span class="occ-tile-icon occ-tile-icon--scope"><i class="fe fe-git-branch"></i></span>
                  <div class="occ-tile-body">
                    <span class="occ-tile-label">Subâmbito</span>
                    <span class="occ-tile-value">
                      @if (item.subScope) {
                        {{ subScopeLabels[item.subScope] }}
                        @if (item.subScope === 'SISTEMA_INTEGRADO' && item.integratedSystems.length) { ({{ systemsLabel(item) }}) }
                        @if (item.subScopeOther) { — {{ item.subScopeOther }} }
                      } @else { <span class="text-muted">Por classificar</span> }
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-sm-6">
                <div class="occ-tile">
                  <span class="occ-tile-icon occ-tile-icon--type"><i class="fe" [class]="typeIcons[item.type]"></i></span>
                  <div class="occ-tile-body">
                    <span class="occ-tile-label">Tipo de ocorrência</span>
                    <span class="occ-tile-value">{{ typeLabels[item.type] }}@if (item.typeOther) { — {{ item.typeOther }} }</span>
                  </div>
                </div>
              </div>
              <!-- A natureza só existe na Participação/Comunicação; fora dela nem se mostra. -->
              @if (item.type === 'PARTICIPACAO_COMUNICACAO') {
                <div class="col-sm-6">
                  <div class="occ-tile">
                    <span class="occ-tile-icon occ-tile-icon--type">
                      <i class="fe" [class]="item.nature ? natureIcons[item.nature] : 'fe-help-circle'"></i>
                    </span>
                    <div class="occ-tile-body">
                      <span class="occ-tile-label">Natureza</span>
                      <span class="occ-tile-value">
                        @if (item.nature) {
                          {{ natureLabels[item.nature] }}
                          @if (item.complaintReason) {
                            · {{ complaintReasonLabels[item.complaintReason] }}
                            @if (item.complaintReasonOther) { — {{ item.complaintReasonOther }} }
                          }
                          @if (item.recurringComplaint) {
                            <span class="badge bg-warning-soft ms-1">Recorrente</span>
                          }
                        } @else { <span class="text-muted">Por classificar</span> }
                      </span>
                    </div>
                  </div>
                </div>
              }
              @if (item.reporterNature) {
                <div class="col-sm-6">
                  <div class="occ-tile">
                    <span class="occ-tile-icon occ-tile-icon--type"><i class="fe fe-message-circle"></i></span>
                    <div class="occ-tile-body">
                      <span class="occ-tile-label">Natureza indicada pelo comunicante</span>
                      <span class="occ-tile-value">
                        {{ reporterNatureLabels[item.reporterNature] }}@if (item.reporterNatureDetail) { — {{ reporterNatureDetailLabels[item.reporterNatureDetail] }} }
                      </span>
                    </div>
                  </div>
                </div>
              }
              <div class="col-sm-6">
                <div class="occ-tile">
                  <span class="occ-tile-icon occ-tile-icon--scope"><i class="fe fe-grid"></i></span>
                  <div class="occ-tile-body">
                    <span class="occ-tile-label">Processo / área</span>
                    <span class="occ-tile-value">
                      @if (item.processArea) {
                        {{ processAreaLabels[item.processArea] }}
                        @if (item.processAreaOther) { — {{ item.processAreaOther }} }
                      } @else { <span class="text-muted">—</span> }
                    </span>
                  </div>
                </div>
              </div>
              <!-- Os dois campos seguintes só existem em certas classificações. Um traço num
                   campo que nunca vai ser preenchido só ocupa a leitura: aparecem quando há o
                   que mostrar, como no formulário. -->
              @if (item.originDetail || item.interestedParty || item.originOther) {
                <div class="col-sm-6">
                  <div class="occ-tile">
                    <span class="occ-tile-icon occ-tile-icon--origin"><i class="fe" [class]="originIcons[item.origin]"></i></span>
                    <div class="occ-tile-body">
                      <span class="occ-tile-label">Detalhe da origem</span>
                      <span class="occ-tile-value">
                        @if (item.originDetail) { {{ originDetailLabels[item.originDetail] }} }
                        @if (item.interestedParty) { · {{ interestedPartyLabels[item.interestedParty] }} }
                        @if (item.originOther) { — {{ item.originOther }} }
                      </span>
                    </div>
                  </div>
                </div>
              }
              @if (item.participantIdentification) {
                <div class="col-sm-6">
                  <div class="occ-tile">
                    <span class="occ-tile-icon occ-tile-icon--origin">
                      <i class="fe" [class]="participantIcons[item.participantIdentification]"></i>
                    </span>
                    <div class="occ-tile-body">
                      <span class="occ-tile-label">Identificação de quem participa</span>
                      <span class="occ-tile-value">
                        {{ participantLabels[item.participantIdentification] }}
                        @if (item.participantName) { — {{ item.participantName }} }
                        @if (item.participantEmail) { ({{ item.participantEmail }}) }
                        @if (item.participantPhone) { · {{ item.participantPhone }} }
                        @if (item.participantRelation) {
                          · {{ relationLabels[item.participantRelation] }}
                          @if (item.participantWorkerType) { ({{ workerTypeLabels[item.participantWorkerType] }}) }
                        }
                        @if (item.participantConfidential) {
                          <span class="badge bg-danger-soft ms-1"><i class="fe fe-lock me-1"></i>Confidencialidade reforçada</span>
                        }
                      </span>
                    </div>
                  </div>
                </div>
              }
            </div>

            <!-- RGPD: titular dos dados -->
            @if (item.dataSubjectName || item.dataSubjectContact || item.dataSubjectRelation || item.dataSubjectValidated !== null) {
              <div class="occ-desc mt-3">
                <h6 class="text-muted text-uppercase mb-2">
                  <i class="fe fe-lock me-1"></i> Titular dos dados (RGPD)
                  @if (item.dataSubjectValidated) {
                    <span class="badge bg-success-soft ms-2">Identidade validada</span>
                  } @else {
                    <span class="badge bg-warning-soft ms-2">Identidade por validar</span>
                  }
                </h6>
                <p class="mb-0">
                  {{ item.dataSubjectName || '—' }}
                  @if (item.dataSubjectContact) { · {{ item.dataSubjectContact }} }
                  @if (item.dataSubjectRelation) {
                    · {{ relationLabels[item.dataSubjectRelation] }}
                    @if (item.dataSubjectWorkerType) { ({{ workerTypeLabels[item.dataSubjectWorkerType] }}) }
                  }
                  @if (item.dataSubjectRelationOther) { — {{ item.dataSubjectRelationOther }} }
                </p>
              </div>
            }

            <!-- Natureza do incidente ou violação: um título só para os dois guardados
                 (documento de 2026-10-08). As categorias do âmbito e, na Segurança da
                 Informação e no registo de violação de dados, as naturezas do incidente. -->
            @if (item.nature === 'DENUNCIA' || item.incidentNatures.length) {
              <div class="mt-3">
                <h6 class="text-muted text-uppercase mb-2"><i class="fe fe-alert-octagon me-1"></i> Natureza do incidente ou violação</h6>
                @if (item.reportCategories.length || item.incidentNatures.length) {
                  @for (category of item.reportCategories; track category) {
                    <span class="badge bg-danger-soft me-1 mb-1">{{ reportCategoryLabels[category] }}</span>
                  }
                  @for (nature of item.incidentNatures; track nature) {
                    <span class="badge bg-danger-soft me-1 mb-1">{{ incidentNatureLabels[nature] }}</span>
                  }
                  @if (item.reportCategoryOther) {
                    <p class="mb-0 mt-2 small"><span class="occ-treat-label">Outra irregularidade:</span> {{ item.reportCategoryOther }}</p>
                  }
                } @else { <p class="text-muted mb-0">—</p> }
              </div>
            }

            <!-- RGPC: visados e conflito de interesses -->
            @if (item.targetedPersons || item.conflictParties) {
              <div class="mt-3">
                @if (item.targetedPersons) {
                  <p class="mb-1 small"><span class="occ-treat-label">Pessoas ou áreas visadas:</span> {{ item.targetedPersons }}</p>
                }
                @if (item.conflictParties) {
                  <p class="mb-0 small"><span class="occ-treat-label">Conflito de interesses — partes:</span> {{ item.conflictParties }}</p>
                }
              </div>
            }
          </div>
        </div>

        <!-- Descrição -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-file-text"></i></span></span>
              <h4 class="card-header-title mb-0">Descrição</h4>
            </div>
            @if (auth.can('occurrence:write')) {
              <a class="btn btn-sm btn-white" routerLink="editar" [queryParams]="{ step: 'descricao' }">
                <i class="fe fe-edit-2 me-1"></i> Editar
              </a>
            }
          </div>
          <div class="card-body">
            <div class="occ-desc mb-3">
              <h6 class="text-muted text-uppercase mb-2"><i class="fe fe-type me-1"></i> Título / assunto</h6>
              <p class="mb-0">{{ item.title }}</p>
            </div>
            <div class="occ-desc">
              <h6 class="text-muted text-uppercase mb-2"><i class="fe fe-align-left me-1"></i> Descrição</h6>
              <p class="mb-0" style="white-space: pre-wrap;">{{ item.description || '—' }}</p>
            </div>

            <!-- A Descrição fica com a data da ocorrência. O prazo da eficácia mudou-se para a
                 Análise e o prazo de reavaliação saiu (documento de 2026-10-08). -->
            <hr class="my-4" />
            <div class="row g-3">
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date">
                  <i class="fe fe-calendar"></i>
                  <div><span class="occ-date-label">Data da ocorrência</span><span class="occ-date-value">{{ item.occurrenceDate | date: 'dd/MM/yyyy' }}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Análise -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-info-soft text-info"><i class="fe fe-search"></i></span></span>
              <h4 class="card-header-title mb-0">Análise</h4>
            </div>
            @if (auth.can('occurrence:write') && startLabel(item, 'EM_ANALISE'); as start) {
              <button class="btn btn-sm btn-primary ms-auto me-2" type="button"
                      [disabled]="changing()" (click)="startState('EM_ANALISE')">
                {{ start }}
              </button>
            }
            @if (auth.can('occurrence:write') && !startLabel(item, 'EM_ANALISE')) {
              <!-- A evidência que se descobre a analisar regista-se daqui, e fica na secção
                   Evidências (documento de 2026-10-08). -->
              <button type="button" class="btn btn-sm btn-white me-2"
                      (click)="registerEvidenceFromAnalysis()">
                <i class="fe fe-plus me-1"></i> Registar evidência
              </button>
              <a class="btn btn-sm btn-white" routerLink="editar" [queryParams]="{ step: 'analise' }">
                <i class="fe fe-edit-2 me-1"></i> Editar
              </a>
            }
          </div>
          <div class="card-body">
            <div class="row g-4">
              <div class="col-12">
                <h6 class="text-muted text-uppercase mb-2">Análise inicial</h6>
                <p class="mb-0" style="white-space: pre-wrap;">{{ item.initialAnalysis || '—' }}</p>
              </div>
              <div class="col-12">
                <h6 class="text-muted text-uppercase mb-2">Análise de causas</h6>
                <p class="mb-0" style="white-space: pre-wrap;">{{ item.causeAnalysis || '—' }}</p>
              </div>
              <!-- A triagem decide o destino de qualquer ocorrência, e vê-se sempre: enquanto a
                   análise não estiver concluída fica "Por decidir" (documento de 2026-10-08). -->
              <div class="col-12">
                <h6 class="text-muted text-uppercase mb-2"><i class="fe fe-filter me-1"></i> Triagem</h6>
                @if (item.triageResult) {
                  <p class="mb-0">
                    <span class="badge"
                          [class.bg-success-soft]="item.triageResult === 'ACEITE'"
                          [class.bg-secondary-soft]="item.triageResult === 'NAO_ACEITE'"
                    >{{ triageResultLabels[item.triageResult] }}</span>
                    @if (item.triageDecidedAt) {
                      <span class="small text-muted ms-2">em {{ item.triageDecidedAt | date: 'dd/MM/yyyy' }}</span>
                    }
                  </p>
                } @else {
                  <p class="mb-0 text-muted">
                    Por decidir@if (item.triageDecisionDeadline) { — prazo até {{ item.triageDecisionDeadline | date: 'dd/MM/yyyy' }}}.
                  </p>
                }
              </div>
              <!-- De qualquer ocorrência desde a revisão de 2026-10-19. Vivia no Tratamento e só nas
                   denúncias e reclamações: o pisco gravava-se e a ficha não o mostrava (QA de
                   2026-10-21). -->
              <div class="col-12">
                <h6 class="text-muted text-uppercase mb-2"><i class="fe fe-clock me-1"></i> À espera de elementos</h6>
                @if (item.awaitingElements) {
                  <p class="mb-0">
                    <span class="badge bg-warning-soft">Aguarda elementos</span>
                    @if (item.elementsRequestedAt) {
                      <span class="small text-muted ms-2">
                        — pedidos em {{ item.elementsRequestedAt | date: 'dd/MM/yyyy' }}@if (item.elementsResponseDeadline) {, resposta até {{ item.elementsResponseDeadline | date: 'dd/MM/yyyy' }}}.
                      </span>
                    }
                  </p>
                } @else {
                  <p class="mb-0 text-muted">Não</p>
                }
              </div>
              <!-- Impacto e requisito vieram da Avaliação, que deixou de existir
                   (documento de 2026-10-03): graduar o caso é parte de o analisar. -->
              <div class="col-12">
                <h6 class="text-muted text-uppercase mb-2">Impacto</h6>
                @if (item.impact) {
                  <span class="badge"
                        [class.bg-danger-soft]="item.impact === 'ALTO'"
                        [class.bg-warning-soft]="item.impact === 'MODERADO'"
                        [class.bg-success-soft]="item.impact === 'BAIXO'"
                        [class.bg-secondary-soft]="item.impact === 'NAO_APLICAVEL' || item.impact === 'NAO_IDENTIFICADO'"
                  >{{ impactLabels[item.impact] }}</span>
                } @else { <p class="mb-0 text-muted">—</p> }
              </div>
              <div class="col-12">
                <h6 class="text-muted text-uppercase mb-2">Requisito</h6>
                <p class="mb-0" style="white-space: pre-wrap;">{{ item.requirementReference || '—' }}</p>
              </div>
              <!-- Veio da Descrição (documento de 2026-10-08): decidir se a eficácia se verifica,
                   e até quando, é parte da análise. -->
              <div class="col-12">
                <h6 class="text-muted text-uppercase mb-2">Verificação da eficácia</h6>
                @if (item.effectivenessEvaluation === 'NAO_APLICAVEL') {
                  <p class="mb-0"><span class="badge bg-secondary-soft">Não aplicável</span></p>
                } @else {
                  <p class="mb-0">
                    Aplicável — prazo p/ aval. da eficácia:
                    <strong>{{ (item.effectivenessEvaluationDeadline | date: 'dd/MM/yyyy') || '—' }}</strong>
                  </p>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Tratamento -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-info-soft text-info"><i class="fe fe-zap"></i></span></span>
              <h4 class="card-header-title mb-0">Tratamento</h4>
            </div>
            @if (auth.can('occurrence:write') && startLabel(item, 'EM_TRATAMENTO'); as start) {
              <button class="btn btn-sm btn-primary ms-auto me-2" type="button"
                      [disabled]="changing()" (click)="startState('EM_TRATAMENTO')">
                {{ start }}
              </button>
            }
            @if (auth.can('occurrence:write') && !startLabel(item, 'EM_TRATAMENTO')) {
              <a class="btn btn-sm btn-white" routerLink="editar" [queryParams]="{ step: 'tratamento' }">
                <i class="fe fe-edit-2 me-1"></i> Editar
              </a>
            }
          </div>
          <div class="card-body">
            <div class="row g-4">
              <div class="col-12">
                <!-- A lista de ações, a mesma do separador Tratamento do formulário. -->
                <app-occurrence-actions
                  #actionsSection
                  [occurrenceId]="item.id"
                  [teamMembers]="teamMembers()"
                  (changed)="onActionsChanged()"
                />
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date" [class.occ-date--warn]="item.deadlineApproaching">
                  <i class="fe fe-flag"></i>
                  <div><span class="occ-date-label">Prazo de implementação</span><span class="occ-date-value">{{ (item.actionImplementationDeadline | date: 'dd/MM/yyyy') || '—' }}</span></div>
                </div>
              </div>
              <!-- Prazos legais próprios: informativos, não geram alerta. -->
              <div class="col-12">
                <div class="alert alert-light border mb-0 py-2">
                  <p class="small mb-2">
                    <i class="fe fe-alert-circle me-1"></i>
                    <strong>Quando exista um prazo legal específico mais curto, é ele que manda</strong>
                    — prevalece sobre os prazos internos acima.
                  </p>
                  <ul class="small text-muted mb-0 ps-3">
                    @for (item of legalDeadlines; track item.situation) {
                      <li>
                        {{ item.situation }}: <strong>{{ item.deadline }}</strong>@if (item.note) {
                          <span class="d-block">{{ item.note }}</span>
                        }
                      </li>
                    }
                  </ul>
                </div>
              </div>
              <!-- Prazo do canal: informar quem participou. A espera de elementos passou para a
                   Análise, onde o pisco vive (QA de 2026-10-21). -->
              @if (isCaseWork()) {
                <div class="col-12 col-sm-6 col-lg-4">
                  <div class="occ-date">
                    <i class="fe fe-send"></i>
                    <div><span class="occ-date-label">Informar quem participou até</span><span class="occ-date-value">{{ (item.informDeadline | date: 'dd/MM/yyyy') || '—' }}</span></div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- RGPD: o titular vive no Essentials, aqui fica a janela e o rasto dos pedidos -->
        @if (rightsRequest(item)) {
          <div class="card">
            <div class="card-header">
              <div class="d-flex align-items-center">
                <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-warning-soft text-warning"><i class="fe fe-user-x"></i></span></span>
                <h4 class="card-header-title mb-0">Titular no Essentials</h4>
              </div>
              @if (auth.can('occurrence:write') && dataSubject()?.summary?.personId && !showErasureForm()) {
                <button class="btn btn-sm btn-white" (click)="openErasureForm()">
                  <i class="fe fe-trash-2 me-1"></i> Pedir eliminação
                </button>
              }
            </div>
            <div class="card-body">
              @if (dataSubjectLoading()) {
                <p class="text-muted mb-0">A consultar o Essentials…</p>
              } @else if (dataSubject(); as lookup) {
                @if (lookup.summary; as subject) {
                  <div class="row g-3 mb-3">
                    <div class="col-md-6">
                      <span class="occ-date-label d-block">Candidato</span>
                      <span class="fw-bold">{{ subject.name || lookup.email }}</span>
                      <span class="small text-muted d-block">{{ lookup.email }}</span>
                    </div>
                    <div class="col-md-6">
                      <span class="occ-date-label d-block">Fundamento da conservação</span>
                      <span class="fw-bold">{{ labelOr(retentionBasisLabels, subject.retentionBasis) }}</span>
                    </div>
                    <div class="col-md-6">
                      <span class="occ-date-label d-block">Informação prestada</span>
                      <span class="fw-bold">
                        {{ subject.informationProvided ? 'Sim' : 'Não' }}
                        @if (subject.informationProvidedAt) {
                          · {{ subject.informationProvidedAt | date: 'dd/MM/yyyy' }}
                        }
                      </span>
                      <span class="small text-muted d-block">
                        {{ labelOr(consentChannelLabels, subject.informationChannel) }}
                      </span>
                    </div>
                    <div class="col-md-6">
                      <span class="occ-date-label d-block">Consentimento de partilha</span>
                      <span class="fw-bold">
                        @if (subject.sharingStatus) {
                          {{ labelOr(consentStatusLabels, subject.sharingStatus) }}
                          @if (subject.sharingRespondedAt) {
                            · {{ subject.sharingRespondedAt | date: 'dd/MM/yyyy' }}
                          }
                        } @else { Não solicitado }
                      </span>
                      <span class="small text-muted d-block">
                        {{ labelOr(consentChannelLabels, subject.sharingChannel) }}
                      </span>
                    </div>
                    @if (subject.objectedAt) {
                      <div class="col-12">
                        <div class="alert alert-warning mb-0 py-2">
                          Oposição exercida em {{ subject.objectedAt | date: 'dd/MM/yyyy' }} — o perfil
                          não pode ser apresentado a clientes.
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <p class="text-muted">{{ lookup.message }}</p>
                }
              } @else {
                <p class="text-muted">
                  Sem consulta ao Essentials. Preencha o contacto do titular com um e-mail para o
                  procurar lá.
                </p>
              }

              @if (showErasureForm()) {
                <div class="border rounded p-3 mb-3">
                  <p class="mb-2">
                    O Essentials elimina os dados pessoais deste candidato <strong>de imediato</strong>,
                    sem período de carência e sem forma de desfazer. Aqui fica o registo do pedido e
                    do fundamento, para a auditoria.
                  </p>
                  <label class="form-label">Fundamento <span class="text-danger">*</span></label>
                  <textarea class="form-control" rows="3" [(ngModel)]="erasureReason"
                            placeholder="Ex.: pedido de apagamento do titular, identidade validada em…"></textarea>
                  @if (erasureError()) { <div class="alert alert-danger small py-2 mt-3 mb-0">{{ erasureError() }}</div> }
                  <div class="d-flex justify-content-end gap-2 mt-3">
                    <button class="btn btn-sm btn-white" (click)="cancelErasureForm()">Cancelar</button>
                    <button class="btn btn-sm btn-danger" (click)="requestErasure()" [disabled]="requestingErasure()">
                      {{ requestingErasure() ? 'A eliminar…' : 'Eliminar no Essentials' }}
                    </button>
                  </div>
                </div>
              }

              @if (dataSubjectRequests().length) {
                <h6 class="text-muted text-uppercase mb-2"><i class="fe fe-file-text me-1"></i> Pedidos ao Essentials</h6>
                <div class="table-responsive">
                  <table class="table table-sm table-nowrap mb-0">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Estado</th>
                        <th class="text-wrap">Fundamento</th>
                        <th class="text-wrap">Detalhe</th>
                        <th>Por</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (request of dataSubjectRequests(); track request.id) {
                        <tr>
                          <td>{{ request.requestedAt | date: 'dd/MM/yyyy HH:mm' }}</td>
                          <td>
                            <span class="badge" [class]="requestStatusClass[request.status]">
                              {{ requestStatusLabels[request.status] }}
                            </span>
                            @if (request.completedAt) {
                              <span class="small text-muted d-block">
                                em {{ request.completedAt | date: 'dd/MM/yyyy HH:mm' }}
                              </span>
                            } @else if (request.scheduledFor) {
                              <span class="small text-muted d-block">
                                para {{ request.scheduledFor | date: 'dd/MM/yyyy' }}
                              </span>
                            }
                          </td>
                          <td class="text-wrap">{{ request.reason || '—' }}</td>
                          <td class="text-wrap">
                            {{ request.detail || '—' }}
                            @if (request.remoteReference) {
                              <span class="small text-muted d-block">{{ request.remoteReference }}</span>
                            }
                          </td>
                          <td>{{ request.requestedByName }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }
            </div>
          </div>
        }

        <!-- Seguimento — histórico de entradas datadas. O conteúdo é o mesmo que o
             separador Seguimento do formulário mostra: um componente só, dois sítios. -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-secondary-soft text-secondary"><i class="fe fe-activity"></i></span></span>
              <h4 class="card-header-title mb-0">Seguimento</h4>
              <span class="badge bg-secondary-soft ms-2">{{ followUps.count() }}</span>
            </div>
          </div>
          <div class="card-body">
            <app-occurrence-follow-ups
              #followUps
              [occurrenceId]="item.id"
              [teamMembers]="teamMembers()"
              [canRegister]="treatmentStarted(item)"
              (changed)="onFollowUpsChanged()"
            />
          </div>
        </div>


        <!-- Verificação da eficácia -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-success-soft text-success"><i class="fe fe-check-circle"></i></span></span>
              <h4 class="card-header-title mb-0">Verificação da eficácia</h4>
            </div>
            @if (auth.can('occurrence:write') && startLabel(item, 'AVALIACAO_REAVALIACAO'); as start) {
              <button class="btn btn-sm btn-primary ms-auto me-2" type="button"
                      [disabled]="changing()" (click)="startState('AVALIACAO_REAVALIACAO')">
                {{ start }}
              </button>
            }
            @if (auth.can('occurrence:write') && !startLabel(item, 'AVALIACAO_REAVALIACAO')) {
              <a class="btn btn-sm btn-white" routerLink="editar" [queryParams]="{ step: 'eficacia' }">
                <i class="fe fe-edit-2 me-1"></i> Editar
              </a>
            }
          </div>
          <div class="card-body">
            <div class="row g-4">
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date">
                  <i class="fe fe-target"></i>
                  <div><span class="occ-date-label">Data esperada da avaliação</span><span class="occ-date-value">{{ (item.effectivenessEvaluationDeadline | date: 'dd/MM/yyyy') || '—' }}</span></div>
                </div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date">
                  <i class="fe fe-award"></i>
                  <div><span class="occ-date-label">Data da avaliação</span><span class="occ-date-value">{{ (item.effectivenessEvaluationDate | date: 'dd/MM/yyyy') || '—' }}</span></div>
                </div>
              </div>
              <div class="col-12">
                <h6 class="text-muted text-uppercase mb-2">Resultado da avaliação</h6>
                @if (item.effectivenessEvaluation) {
                  <span
                    class="badge"
                    [class.bg-success-soft]="item.effectivenessEvaluation === 'EFICAZ'"
                    [class.bg-danger-soft]="item.effectivenessEvaluation === 'NAO_EFICAZ'"
                    [class.bg-secondary-soft]="item.effectivenessEvaluation === 'NAO_APLICAVEL'"
                    [class.bg-warning-soft]="item.effectivenessEvaluation === 'NAO_FOI_POSSIVEL_AFERIR'"
                  >{{ effectivenessLabels[item.effectivenessEvaluation] }}</span>
                  @if (item.effectivenessUnableReason) {
                    <p class="mb-0 mt-2"><span class="occ-treat-label">Motivo:</span> {{ item.effectivenessUnableReason }}</p>
                  }
                } @else { <p class="mb-0 text-muted">—</p> }
              </div>
              <div class="col-12">
                <h6 class="text-muted text-uppercase mb-2">Constatações da avaliação</h6>
                <p class="mb-0" style="white-space: pre-wrap;">{{ item.effectivenessFindings || '—' }}</p>
              </div>
              @if (auth.can('occurrence:write')) {
                <div class="col-12">
                  <!-- "Necessidade de criar nova ocorrência?" — a mesma ligação das derivadas. -->
                  @if (hasActions()) {
                    <a class="btn btn-sm btn-white" [routerLink]="['..', 'nova']" [queryParams]="{ parentId: item.id }">
                      <i class="fe fe-git-branch me-1"></i> Abrir ocorrência a partir desta avaliação
                    </a>
                  } @else {
                    <button type="button" class="btn btn-sm btn-white" disabled title="Sem ações no tratamento não há ocorrências derivadas. Acrescente primeiro uma ação.">
                      <i class="fe fe-git-branch me-1"></i> Abrir ocorrência a partir desta avaliação
                    </button>
                  }
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Comunicações — a thread de e-mail da ocorrência (o código no assunto traz as
             respostas de volta para aqui) -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-info-soft text-info"><i class="fe fe-mail"></i></span></span>
              <h4 class="card-header-title mb-0">Comunicações</h4>
              <span class="badge bg-secondary-soft ms-2">{{ messages().length }}</span>
            </div>
            @if (auth.can('occurrence:write') && !showMessageForm()) {
              <button class="btn btn-sm btn-white" (click)="openMessageForm()">
                <i class="fe fe-send me-1"></i> Enviar e-mail
              </button>
            }
          </div>
          <div class="card-body">
            @if (showMessageForm()) {
              <div class="border rounded p-3 mb-3" id="form-comunicacao">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Destino</label>
                    <select class="form-select" [(ngModel)]="newMessageAudience" data-no-filter>
                      <option value="EXTERNA">Para fora (leva o link da conversa)</option>
                      <option value="PORTAL">Acompanhamento (quem participou pelo site)</option>
                      <option value="INTERNA">Interna (fica na equipa)</option>
                    </select>
                  </div>
                  @if (newMessageAudience !== 'PORTAL') {
                    <div class="col-md-8">
                      <label class="form-label">Para <span class="text-danger">*</span></label>
                      <input class="form-control" type="email" [(ngModel)]="newMessageTo"
                             placeholder="destinatario@exemplo.pt" />
                    </div>
                  } @else {
                    <div class="col-md-8 d-flex align-items-end">
                      <p class="text-muted small mb-2">
                        Não sai daqui nenhum e-mail: a mensagem fica à espera na página de
                        acompanhamento, e quem participou lê-a e responde com o identificador —
                        sem se identificar.
                      </p>
                    </div>
                  }
                  <div class="col-12">
                    <label class="form-label">Assunto <span class="text-danger">*</span></label>
                    <input class="form-control" [(ngModel)]="newMessageSubject" />
                    <small class="text-muted">
                      O código entre parênteses traz a resposta de volta a esta ocorrência.
                      @if (newMessageAudience === 'EXTERNA') {
                        A mensagem leva também o link por onde a outra parte lê e responde.
                      }
                    </small>
                  </div>
                  <div class="col-12">
                    <label class="form-label">Mensagem <span class="text-danger">*</span></label>
                    <textarea class="form-control" rows="5" [(ngModel)]="newMessageBody"></textarea>
                  </div>
                </div>
                @if (messageError()) { <div class="alert alert-danger small py-2 mt-3 mb-0">{{ messageError() }}</div> }
                <div class="d-flex justify-content-end gap-2 mt-3">
                  <button class="btn btn-sm btn-white" (click)="cancelMessageForm()">Cancelar</button>
                  <button class="btn btn-sm btn-primary" (click)="sendMessage()" [disabled]="sendingMessage()">
                    {{ sendingMessage() ? 'A enviar…' : 'Enviar' }}
                  </button>
                </div>
              </div>
            }
            @if (conversationLinks().length) {
              <!-- O link é a porta de quem está de fora: fica à mão para reenviar a quem o perdeu. -->
              <div class="border rounded p-3 mb-3">
                <h6 class="text-muted text-uppercase mb-2"><i class="fe fe-link me-1"></i> Links da conversa</h6>
                @for (conversation of conversationLinks(); track conversation.id) {
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <span class="small flex-grow-1 min-w-0 text-truncate">
                      <strong>{{ conversation.externalAddress }}</strong>
                      <span class="text-muted"> — {{ conversation.link }}</span>
                    </span>
                    <button class="btn btn-sm btn-white" (click)="copyLink(conversation.link)">
                      {{ copiedLink() === conversation.link ? 'Copiado' : 'Copiar' }}
                    </button>
                  </div>
                }
              </div>
            }
            @if (messages().length === 0) {
              <p class="text-muted mb-0">
                Sem comunicações. Os e-mails enviados daqui ficam registados, as respostas voltam
                a esta lista pelo código no assunto, e quem está de fora pode responder pelo link.
              </p>
            } @else {
              <!-- Em conversas: a mensagem que a abriu e as respostas por baixo, com o mesmo
                   assunto e um "Responder" à frente (revisão de 2026-10-19). -->
              <div class="list-group list-group-flush">
                @for (thread of threads(); track thread.key) {
                  <div class="list-group-item px-0">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <span class="fw-bold flex-grow-1 min-w-0 text-truncate">
                        {{ thread.subject }}
                        @if (thread.audience === 'INTERNA') {
                          <span class="badge bg-secondary-soft ms-1">Interna</span>
                        } @else if (thread.audience === 'PORTAL') {
                          <span class="badge bg-info-soft ms-1">Acompanhamento</span>
                        }
                        @if (thread.messages.length > 1) {
                          <span class="badge bg-light text-body border ms-1">{{ thread.messages.length }}</span>
                        }
                      </span>
                      @if (auth.can('occurrence:write')) {
                        <button class="btn btn-sm btn-white text-nowrap" (click)="replyTo(thread)">
                          <i class="fe fe-corner-up-left me-1"></i> Responder
                        </button>
                      }
                    </div>
                    @for (message of thread.messages; track message.id; let first = $first) {
                      <div class="d-flex align-items-start gap-3" [class.mt-2]="!first" [class.ms-4]="!first">
                        <span class="avatar avatar-sm">
                          <span class="avatar-title fs-lg rounded"
                                [class.bg-primary-soft]="message.direction === 'ENVIADA'"
                                [class.text-primary]="message.direction === 'ENVIADA'"
                                [class.bg-success-soft]="message.direction === 'RECEBIDA'"
                                [class.text-success]="message.direction === 'RECEBIDA'">
                            <i class="fe" [class.fe-arrow-up-right]="message.direction === 'ENVIADA'"
                               [class.fe-arrow-down-left]="message.direction === 'RECEBIDA'"></i>
                          </span>
                        </span>
                        <div class="flex-grow-1 min-w-0">
                          <span class="small text-muted d-block">
                            {{ message.sentAt | date: 'dd/MM/yyyy HH:mm' }} ·
                            {{ message.fromAddress }} <i class="fe fe-arrow-right mx-1"></i> {{ message.toAddress }}
                            @if (message.sentByName) { · {{ message.sentByName }} }
                          </span>
                          <!-- A conversa é para se percorrer, não para se ler de uma vez: o corpo
                               fica fechado e abre a pedido (documento de 2026-09-26). -->
                          @if (expandedMessages().has(message.id)) {
                            <p class="mb-0 mt-2 occ-prewrap">{{ message.body }}</p>
                          }
                          <button class="btn btn-link btn-sm px-0" (click)="toggleMessage(message.id)">
                            <i class="fe me-1"
                               [class.fe-chevron-down]="!expandedMessages().has(message.id)"
                               [class.fe-chevron-up]="expandedMessages().has(message.id)"></i>
                            {{ expandedMessages().has(message.id) ? 'Fechar' : 'Ver mensagem' }}
                          </button>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>

        <!-- Ligações mãe↔filha (derivadas) — sempre visível: era a única porta para as derivadas
             e só aparecia depois de a ligação existir. -->
        @if (links(); as linked) {
            <div class="card">
              <div class="card-header">
                <div class="d-flex align-items-center">
                  <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-info-soft text-info"><i class="fe fe-link"></i></span></span>
                  <h4 class="card-header-title mb-0">Ligações</h4>
                  <!-- A ocorrência aberta em resposta a outra: a ligação vive aqui, não no
                       formulário, porque só faz sentido depois de o registo existir. -->
                </div>
                @if (auth.can('occurrence:write')) {
                  @if (hasActions()) {
                    <a class="btn btn-sm btn-white" [routerLink]="['..', 'nova']" [queryParams]="{ parentId: item.id }">
                      <i class="fe fe-git-branch me-1"></i> Abrir ocorrência derivada
                    </a>
                  } @else {
                    <button type="button" class="btn btn-sm btn-white" disabled title="Sem ações no tratamento não há ocorrências derivadas. Acrescente primeiro uma ação.">
                      <i class="fe fe-git-branch me-1"></i> Abrir ocorrência derivada
                    </button>
                  }
                }
              </div>
              <div class="card-body">
                @if (justClosed()) {
                  <div class="alert alert-info d-flex align-items-center gap-2">
                    <i class="fe fe-git-branch"></i>
                    <span>Ocorrência fechada. Quer abrir uma ocorrência derivada para responder a esta?</span>
                  </div>
                }
                @if (!linked.parent && !linked.children.length) {
                  <p class="text-muted mb-0">
                    Sem ligações. Use “Abrir ocorrência derivada” para registar uma ação de resposta a esta
                    ocorrência — a ligação fica permanente.
                  </p>
                }
                @if (linked.parent; as parent) {
                  <h6 class="text-muted text-uppercase mb-2">Derivada da ocorrência</h6>
                  <div class="list-group list-group-flush mb-3">
                    <a class="list-group-item px-0 d-flex align-items-center gap-3" [routerLink]="['..', parent.id]">
                      <span class="avatar avatar-sm"><span class="avatar-title fs-lg rounded bg-info-soft text-info"><i class="fe fe-corner-left-up"></i></span></span>
                      <div class="flex-grow-1 min-w-0">
                        <span class="fw-bold d-block text-truncate">{{ parent.code }} — {{ parent.title }}</span>
                        <!-- Momento da criação (documento de 2026-10-08): a ligação nasce com a
                             ocorrência derivada, e saber quando situa a ligação no tempo. -->
                        <span class="small text-muted">Criada em {{ parent.createdAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                      </div>
                      <span class="badge" [class]="linkBadgeClass(parent)">{{ linkBadgeLabel(parent) }}</span>
                    </a>
                  </div>
                }
                @if (linked.children.length) {
                  <h6 class="text-muted text-uppercase mb-2">Ocorrências derivadas desta</h6>
                  <div class="list-group list-group-flush">
                    @for (child of linked.children; track child.id) {
                      <a class="list-group-item px-0 d-flex align-items-center gap-3" [routerLink]="['..', child.id]">
                        <span class="avatar avatar-sm"><span class="avatar-title fs-lg rounded bg-info-soft text-info"><i class="fe fe-corner-right-down"></i></span></span>
                        <div class="flex-grow-1 min-w-0">
                          <span class="fw-bold d-block text-truncate">{{ child.code }} — {{ child.title }}</span>
                          <span class="small text-muted">Criada em {{ child.createdAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                        </div>
                        <span class="badge" [class]="linkBadgeClass(child)">{{ linkBadgeLabel(child) }}</span>
                      </a>
                    }
                  </div>
                }
              </div>
            </div>
        }

        <!-- Evidências — entradas com data, descrição, notas e ficheiros. O mesmo componente do
             separador Evidências do formulário (revisão de 2026-10-13). -->
        <div class="card" id="seccao-evidencias">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-warning-soft text-warning"><i class="fe fe-paperclip"></i></span></span>
              <h4 class="card-header-title mb-0">Evidências</h4>
              <span class="badge bg-secondary-soft ms-2">{{ evidencesSection.count() }}</span>
            </div>
          </div>
          <div class="card-body">
            <app-occurrence-evidences #evidencesSection [occurrenceId]="item.id" />
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-4">
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-shuffle"></i></span></span>
              <h4 class="card-header-title mb-0">Estado</h4>
            </div>
          </div><!-- card-header -->
          <div class="card-body">
            @if (item.deadlineApproaching) {
              <div class="alert alert-warning small"><i class="fe fe-alert-triangle me-1"></i> Prazo a expirar em breve.</div>
            }
            <!-- Situação e estado são campos distintos: fechar não apaga o ponto do ciclo em que o
                 tratamento parou. -->
            <label class="form-label">Situação</label>
            <select class="form-select mb-3" [(ngModel)]="newSituation" (ngModelChange)="onStateSelected()" data-no-filter>
              @for (option of situationOptions; track option.value) {
                <option [ngValue]="option.value">
                  {{ option.label }}{{ option.value === item.situation ? ' (atual)' : '' }}
                </option>
              }
            </select>

            <label class="form-label">Estado</label>
            <!-- Ordem do ciclo de vida (era alfabética); o estado atual fica marcado. -->
            <select class="form-select mb-3" [(ngModel)]="newState" (ngModelChange)="onStateSelected()">
              @for (option of stateOptions; track option.value) {
                <option [ngValue]="option.value">
                  {{ option.label }}{{ option.value === item.state ? ' (atual)' : '' }}
                </option>
              }
            </select>

            @if (newSituation === 'FECHADO') {
              <label class="form-label">Tipo de fecho <span class="text-danger">*</span></label>
              <select class="form-select mb-3" [(ngModel)]="newClosingType" data-no-filter>
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of closingTypeOptions; track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              <!-- O desfecho deixou de ser exclusivo das denúncias e reclamações (2026-09-26). -->
              <label class="form-label">Desfecho <span class="text-danger">*</span></label>
              <select class="form-select mb-3" [(ngModel)]="newOutcome" data-no-filter>
                <option [ngValue]="null" disabled>Selecione…</option>
                @for (option of outcomeOptions; track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
              @if (newOutcome === 'OUTRO') {
                <p class="small text-muted mb-3">Descreva o desfecho no resultado, abaixo.</p>
              }
              <!-- Obrigatório só com o desfecho "Outro" (revisão de 2026-10-19). -->
              <label class="form-label">
                Resultado @if (newOutcome === 'OUTRO') { <span class="text-danger">*</span> }
              </label>
              <textarea class="form-control mb-3" rows="3" [(ngModel)]="newClosingResult"
                        placeholder="O que ficou apurado ao fechar a ocorrência."></textarea>
              <label class="form-label">Data de conclusão <span class="text-muted small">(default hoje)</span></label>
              <app-date-picker class="mb-3 d-block" [(ngModel)]="newClosingDate" />
            }

            @if (stateError()) { <div class="alert alert-danger small py-2">{{ stateError() }}</div> }

            @if (auth.can('occurrence:write')) {
              <button class="btn btn-primary w-100" (click)="applyState()"
                      [disabled]="changing() || (newState === item.state && newSituation === item.situation)">
                {{ changing() ? 'A aplicar…' : 'Aplicar' }}
              </button>
            }
            <p class="text-muted small mt-2 mb-0">
              Em avaliação preenche o prazo (+3 meses, editável). Fechar exige o tipo de fecho
              e o desfecho — e o resultado, quando o desfecho é "Outro" —, e faz-se a partir de Em avaliação — ou de Em seguimento,
              quando a verificação da eficácia não é aplicável — salvo quando o desfecho é "não
              aceite neste canal" ou "encerrado por falta de elementos". A eficácia não é
              precisa para fechar: com ela por fazer, o fecho é "Sujeito a verificação". Reabrir
              limpa o fecho. De Registado não se passa direto a Em tratamento.
            </p>
          </div>
        </div>

        <!-- Histórico de reclassificações (triagem/análise) -->
        <div class="card">
          <div class="card-header">
            <div class="d-flex align-items-center">
              <span class="avatar avatar-sm me-3"><span class="avatar-title fs-lg rounded bg-secondary-soft text-secondary"><i class="fe fe-clock"></i></span></span>
              <h4 class="card-header-title mb-0">Histórico</h4>
            </div>
            <span class="badge bg-secondary-soft">{{ historyEntries().length }}</span>
          </div>
          <div class="card-body">
            @if (historyEntries().length === 0) {
              <p class="text-muted small mb-0">Sem reclassificações registadas.</p>
            } @else {
              @for (entry of historyEntries(); track entry.id) {
                <div class="ticket-audit-item">
                  <span class="ticket-audit-dot"></span>
                  <div class="ticket-audit-body">
                    <p class="small mb-0">
                      <strong>{{ auditFieldLabels[entry.field] ?? entry.field }}</strong>:
                      {{ auditValue(entry.field, entry.oldValue) }}
                      <i class="fe fe-arrow-right mx-1"></i>
                      {{ auditValue(entry.field, entry.newValue) }}
                    </p>
                    <span class="small text-muted">{{ entry.performedByName }} · {{ entry.createdAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                  </div>
                </div>
              }
            }
          </div>
        </div>
      </div>
    </div>
  </div>
}

<!-- Painel de edição: rota filha, para a ocorrência ficar montada por trás. -->
<!-- O painel de edição é uma rota filha: a ficha por baixo nunca é destruída, e voltar dele não
     muda o parâmetro da rota — nada avisava que havia o que recarregar, e ficava-se a olhar para
     os valores antigos depois de gravar. Fechar o painel recarrega. -->
<router-outlet (deactivate)="reloadAfterEdit()" />
